import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMode } from '../../contexts/AppModeContext';
import { useCanvasData } from '../../contexts/CanvasDataContext';
import { updateDefaultUser } from '../../services/canvasService';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useSelectedChildName } from '../../hooks/useSelectedChildName';
import HamburgerButton from './HamburgerButton';
import '../../styles/common.css';
import '../../styles/sidebar-improvements.css';

// The width at which the sidebar stops being a docked column and becomes an
// off-canvas overlay drawer. That switch is made by styles/common.css's
// `@media (max-width: 767px)` block -- `.sidebar { transform: translateX(-100%) }`
// with `.sidebar.open { transform: translateX(0) }` -- so 767px, not the 768px
// used by styles/sidebar-improvements.css, whose mobile block only resizes the
// drawer and reveals its labels.
//
// The distinction matters: focus may only be trapped when the drawer is
// overlaying the page. At exactly 768px the sidebar is still docked and the
// content beside it must stay reachable by Tab.
const MOBILE_DRAWER_QUERY = '(max-width: 767px)';

// Modern icon components
const DashboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
  </svg>
);

const BookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);

const CameraIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);

const ImportIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const OfflineIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 9 9 9 9 0 0 1-9 9 9 9 0 0 1-9-9z"/>
    <path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18z"/>
    <line x1="3" y1="3" x2="21" y2="21"/>
  </svg>
);

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{
      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }}
  >
    <polyline points="9,18 15,12 9,6"/>
  </svg>
);

const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const ActionPlanIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9"/>
    <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const Sidebar = () => {
  // Get initial state from localStorage with default of 'open'
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem('sidebarOpen');
    return savedState !== null ? savedState === 'true' : true;
  });

  const [showConcluded, setShowConcluded] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [showCourseSearch, setShowCourseSearch] = useState(false);
  const [userSelectorOpen, setUserSelectorOpen] = useState(false);
  const [userSwitchLoading, setUserSwitchLoading] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);

  // Whether the sidebar is currently rendered as the mobile overlay drawer.
  // Read from matchMedia rather than from a width guess so it tracks the CSS
  // breakpoint exactly, and stays in sync if the device is rotated.
  const [isMobileDrawer, setIsMobileDrawer] = useState(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(MOBILE_DRAWER_QUERY).matches
  );

  const sidebarRef = useRef(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentUser, logout, availableUsers, switchCanvasUser, refreshAvailableUsers } = useAuth();
  const {
    coursesMode,
    guestAvailableUsers,
    getActiveCredentials,
    dashboardPath: dashboardUrl,
    offlineSnapshot,
    switchOfflineChild,
  } = useAppMode();
  const isOfflineMode = coursesMode === 'offline';

  // Task 6.0s. Offline mode has no live observees call to fill
  // `availableUsers`, so the list came up empty. The 6.0f child index is the
  // same source the Offline Mode entry page uses for its picker; the shape is
  // normalised to what the dropdown below already renders (`id`, `name`).
  // Task 5.8: the child index is loaded by useSelectedChildName, which the
  // header shares, so the name and this dropdown read one list.
  const { name: selectedChildName, offlineChildren } = useSelectedChildName();
  const [offlineSwitchError, setOfflineSwitchError] = useState('');

  useEffect(() => {
    if (!isOfflineMode) {
      setOfflineSwitchError('');
    }
  }, [isOfflineMode]);

  const formatSnapshotDate = (iso) => {
    if (!iso) return null;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
  };
  const {
    courses,
    initializing,
    refreshAllData,
    switchGuestUser,
    // The session's Canvas user in every mode, offline included (task 6.0t).
    userId: sessionUserId,
  } = useCanvasData();

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarOpen', sidebarOpen);
    // Add/remove class to body for styling purposes
    if (sidebarOpen) {
      document.body.classList.remove('sidebar-closed');
    } else {
      document.body.classList.add('sidebar-closed');
    }
  }, [sidebarOpen]);

  // Track the mobile breakpoint.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia(MOBILE_DRAWER_QUERY);
    const handleChange = (event) => setIsMobileDrawer(event.matches);

    setIsMobileDrawer(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Toggle sidebar handler
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Trap focus in the drawer only while it is actually behaving as a modal
  // overlay -- open AND at the mobile breakpoint. On desktop the sidebar is
  // open by default and the page beside it is still reachable, so trapping
  // there would strand keyboard users in the navigation with no way out.
  useFocusTrap(sidebarRef, sidebarOpen && isMobileDrawer, () => setSidebarOpen(false));

  // Handle user selection
  const handleUserSelect = async (userId) => {
    if (userSwitchLoading) return;

    setUserSwitchLoading(true);
    setUserSelectorOpen(false);

    try {
      if (isOfflineMode) {
        // Task 6.0s. No credentials change and nothing to refresh: the
        // context swaps the stored snapshot on screen. Land on the dashboard
        // because the course the owner was reading is not this child's course.
        setOfflineSwitchError('');
        const result = await switchOfflineChild(userId);
        if (result.ok) {
          navigate(dashboardUrl);
        } else {
          setOfflineSwitchError(result.reason || 'Could not switch child.');
        }
        return;
      }

      if (coursesMode === 'guest') {
        await switchGuestUser(userId);
      } else {
        await switchCanvasUser(userId);
      }

      await refreshAllData();
    } catch (error) {
      console.error('Error switching user:', error);
    } finally {
      setUserSwitchLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.href = '/login';
    }
  };

  // Handle setting a user as default
  const handleSetAsDefault = async (userId, event) => {
    event.stopPropagation(); // Prevent user selection when clicking "Set as Default"

    if (settingDefault || coursesMode === 'guest') return; // Don't allow setting defaults in guest mode

    setSettingDefault(true);

    try {
      // Update the availableUsers array to reflect new default
      const updatedUsers = availableUsers.map(user => ({
        ...user,
        default: user.id.toString() === userId.toString()
      }));

      // Call the backend to update the default user preference
      await updateDefaultUser(userId, updatedUsers);

      // Task 4.4b: this used to rely on apiServiceManager clearing its
      // tag-based cache for `user_${userId}` (which incidentally also
      // cleared that user's course/courseDetails/courseHistory entries,
      // since they shared the tag). That cache is gone; invalidate the
      // equivalent TanStack Query entries directly so the newly-default
      // user's course data refreshes rather than serving anything stale
      // from before the switch. Default-user switching is an
      // authenticated-only action (disabled in guest mode above), so
      // 'auth' is the only mode that can apply here.
      const newDefaultUserId = userId.toString();
      queryClient.invalidateQueries({ queryKey: ['courses', 'auth', newDefaultUserId] });
      queryClient.invalidateQueries({ queryKey: ['courseDetails', 'auth', newDefaultUserId] });
      queryClient.invalidateQueries({ queryKey: ['courseHistory', 'auth', newDefaultUserId] });

      // Update session storage
      sessionStorage.setItem("availableUsers", JSON.stringify(updatedUsers));

      // Refresh the available users in AuthContext to update the UI
      refreshAvailableUsers();

      console.log(`Set user ${userId} as default`);
    } catch (error) {
      console.error('Error setting default user:', error);
    } finally {
      setSettingDefault(false);
    }
  };
  
  // Use useMemo to filter courses efficiently and re-calculate when courses change
  const { activeCourses, concludedCourses, filteredActiveCourses } = useMemo(() => {
    if (!courses || !Array.isArray(courses) || courses.length === 0) {
      return { activeCourses: [], concludedCourses: [], filteredActiveCourses: [] };
    }

    const active = courses.filter(course => !course.concluded && !course.access_restricted);
    const concluded = courses.filter(course => course.concluded);

    // Filter active courses based on search term
    const filteredActive = courseSearchTerm
      ? active.filter(course =>
          course.name.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
          course.course_code?.toLowerCase().includes(courseSearchTerm.toLowerCase())
        )
      : active;

    return { activeCourses: active, concludedCourses: concluded, filteredActiveCourses: filteredActive };
  }, [courses, courseSearchTerm]); // This ensures recalculation when courses or search term change
  
  // Log courses changes for debugging
  // useEffect(() => {
  //   console.log("Sidebar - courses updated:", courses?.length);
  // }, [courses]);

  // Don't show sidebar during initialization
  if (initializing) {
    return <div className={`sidebar ${sidebarOpen ? '' : 'closed'} sidebar-disabled`}></div>;
  }
  
  // // Don't show full sidebar during user selection
  // if (pendingUserSelection) {
  //   return <div className={`sidebar ${sidebarOpen ? '' : 'closed'} sidebar-disabled`}></div>;
  // }

  

  return (
    <>
      {/* Hamburger button - fixed on left side */}
      <div className="sidebar-hamburger-wrapper">
        <HamburgerButton
          isOpen={sidebarOpen}
          onClick={toggleSidebar}
          ariaLabel={sidebarOpen ? "Close navigation" : "Open navigation"}
          controlsId="sidebar-nav"
        />
      </div>

      <div
        id="sidebar-nav"
        ref={sidebarRef}
        className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="sidebar-inner">
          {/* Canvas User Selector */}
          {(currentUser || coursesMode === 'guest' || isOfflineMode) && (
            <div className="sidebar-section sidebar-user-section">
              <AnimatePresence>
                {sidebarOpen && (
                  <motion.div
                    className="sidebar-user-selector"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <h3 className="sidebar-title sidebar-user-title">
                      <span className="sidebar-icon">
                        <UsersIcon />
                      </span>
                      <span className="sidebar-truncate">Canvas User</span>
                    </h3>
                    <button
                      className="sidebar-user-button"
                      onClick={() => setUserSelectorOpen(!userSelectorOpen)}
                      disabled={userSwitchLoading}
                      aria-label="Select Canvas user"
                      aria-expanded={userSelectorOpen}
                    >
                      <span className="sidebar-user-name">
                        {userSwitchLoading ? 'Switching...' : selectedChildName}
                      </span>
                      <ChevronIcon expanded={userSelectorOpen} />
                    </button>
                    {/* Task 6.0s. The snapshot on screen is a point in time,
                        and switching children picks the nearest one; showing
                        the date here is what makes a distant match visible. */}
                    {isOfflineMode && formatSnapshotDate(offlineSnapshot?.date) && (
                      <p className="sidebar-user-subtitle" data-testid="offline-snapshot-date">
                        Snapshot from {formatSnapshotDate(offlineSnapshot?.date)}
                      </p>
                    )}
                    {isOfflineMode && offlineSwitchError && (
                      <p className="sidebar-user-error" role="alert">{offlineSwitchError}</p>
                    )}

                    <AnimatePresence>
                      {userSelectorOpen && (
                        <motion.div
                          className="sidebar-user-dropdown"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="sidebar-user-list">
                            {(isOfflineMode
                              ? offlineChildren
                              : coursesMode === 'guest' ? guestAvailableUsers : availableUsers
                            )?.map(user => {
                              const credentials = getActiveCredentials();
                              const isCurrentUser = isOfflineMode
                                ? String(sessionUserId) === user.id.toString()
                                : credentials?.userId === user.id.toString();
                              const isDefaultUser = user.default === true;

                              return (
                                <div key={user.id} className="sidebar-user-item-wrapper">
                                  <button
                                    className={`sidebar-user-item ${isCurrentUser ? 'active' : ''} ${isDefaultUser ? 'default-user' : ''}`}
                                    onClick={() => handleUserSelect(user.id)}
                                    disabled={userSwitchLoading}
                                  >
                                    <span className="sidebar-user-item-name">
                                      {user.name}
                                      {isDefaultUser && <span className="sidebar-default-indicator" title="Default User">★</span>}
                                    </span>
                                    {isCurrentUser && <span className="sidebar-user-check">✓</span>}
                                  </button>

                                  {/* Set as Default button - only show for authenticated users and if not already default.
                                      Not in offline mode either (6.0s): the default is a live-Canvas concept. */}
                                  {coursesMode !== 'guest' && !isOfflineMode && !isDefaultUser && (
                                    <button
                                      className="sidebar-set-default-button"
                                      onClick={(e) => handleSetAsDefault(user.id, e)}
                                      disabled={settingDefault}
                                      title="Set as Default User"
                                      aria-label={`Set ${user.name} as default user`}
                                    >
                                      {settingDefault ? '...' : '⭐'}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>

            </div>
          )}

          {/* Dashboard Link */}
          <div className="sidebar-section">
            <ul className="sidebar-menu">
              <li>
                <NavLink
                  to={dashboardUrl}
                  className="sidebar-link"
                  end
                >
                  <motion.span
                    className="sidebar-icon"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <DashboardIcon />
                  </motion.span>
                  <AnimatePresence>
                    {sidebarOpen && (
                      <motion.span
                        className="sidebar-text"
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        Dashboard
                      </motion.span>
                    )}
                  </AnimatePresence>
                </NavLink>
              </li>
            </ul>
          </div>

          {/* Active Courses Section */}
          {activeCourses.length > 0 && (
            <div className="sidebar-section">
              <AnimatePresence>
                {sidebarOpen && (
                  <motion.div
                    className="sidebar-section-header"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <h3 className="sidebar-title">
                      <span className="sidebar-truncate">Courses</span>
                      <span className="sidebar-course-count">{activeCourses.length}</span>
                    </h3>
                    {activeCourses.length > 5 && (
                      <button
                        className="sidebar-search-toggle"
                        onClick={() => setShowCourseSearch(!showCourseSearch)}
                        title="Search courses"
                        aria-label="Toggle course search"
                        aria-expanded={showCourseSearch}
                      >
                        <SearchIcon />
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {showCourseSearch && sidebarOpen && (
                  <motion.div
                    className="sidebar-search"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <input
                      type="text"
                      placeholder="Search courses..."
                      value={courseSearchTerm}
                      onChange={(e) => setCourseSearchTerm(e.target.value)}
                      className="sidebar-search-input"
                      autoFocus
                      aria-label="Search courses"
                    />
                    {courseSearchTerm && (
                      <button
                        className="sidebar-search-clear"
                        onClick={() => setCourseSearchTerm('')}
                        aria-label="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <ul className="sidebar-menu">
                {filteredActiveCourses.map(course => (
                  <li key={course.api_course_id}>
                    <NavLink
                      to={`/course/${course.api_course_id}`}
                      className="sidebar-link"
                      title={course.name}
                    >
                      <motion.span
                        className="sidebar-icon"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <BookIcon />
                      </motion.span>
                      <AnimatePresence>
                        {sidebarOpen && (
                          <>
                            <motion.span
                              className="sidebar-text sidebar-truncate"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {course.name}
                            </motion.span>
                            {course.course_code && (
                              <motion.span
                                className="sidebar-course-code"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                {course.course_code}
                              </motion.span>
                            )}
                          </>
                        )}
                      </AnimatePresence>
                    </NavLink>
                  </li>
                ))}
                {courseSearchTerm && filteredActiveCourses.length === 0 && sidebarOpen && (
                  <li className="sidebar-no-results">
                    <span className="sidebar-text">No courses found</span>
                  </li>
                )}
              </ul>
            </div>
          )}
          
          {/* Concluded Courses Section - Collapsible */}
          {concludedCourses.length > 0 && sidebarOpen && (
            <div className="sidebar-section">
              <motion.h3
                className="sidebar-title sidebar-title-collapsible"
                onClick={() => setShowConcluded(!showConcluded)}
                whileHover={{ backgroundColor: 'var(--gray-100)' }}
                transition={{ duration: 0.2 }}
              >
                <span className="sidebar-title-content">
                  <span className="sidebar-truncate">Concluded</span>
                  <span className="sidebar-course-count">{concludedCourses.length}</span>
                </span>
                <ChevronIcon expanded={showConcluded} />
              </motion.h3>

              <AnimatePresence>
                {showConcluded && (
                  <motion.ul
                    className="sidebar-menu concluded-courses"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  >
                    {concludedCourses.map(course => (
                      <li key={course.api_course_id}>
                        <NavLink
                          to={`/course/${course.api_course_id}`}
                          className="sidebar-link"
                          title={course.name}
                        >
                          <motion.span
                            className="sidebar-icon"
                            whileHover={{ scale: 1.1 }}
                            transition={{ duration: 0.2 }}
                          >
                            <BookIcon />
                          </motion.span>
                          <span className="sidebar-text sidebar-truncate">{course.name}</span>
                          {course.course_code && (
                            <span className="sidebar-course-code">{course.course_code}</span>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          )}
          
          {/* Tools Section */}
          {(currentUser || coursesMode === 'guest') && (
            <div className="sidebar-section">
              <AnimatePresence>
                {sidebarOpen && (
                  <motion.h3
                    className="sidebar-title"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    Tools
                  </motion.h3>
                )}
              </AnimatePresence>
              <ul className="sidebar-menu">
                <li>
                  <NavLink
                    to="/snapshots"
                    className="sidebar-link"
                  >
                    <motion.span
                      className="sidebar-icon"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CameraIcon />
                    </motion.span>
                    <AnimatePresence>
                      {sidebarOpen && (
                        <motion.span
                          className="sidebar-text"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          Grade Snapshots
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </NavLink>
                </li>
                {/* Task 6.0b. Deliberately not gated on having Canvas
                    credentials: importing a hand-captured snapshot is what a
                    user does precisely *because* they have no working token,
                    so hiding it behind credentials would hide it from everyone
                    who needs it. */}
                <li>
                  <NavLink
                    to="/snapshots/import"
                    className="sidebar-link"
                  >
                    <motion.span
                      className="sidebar-icon"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ImportIcon />
                    </motion.span>
                    <AnimatePresence>
                      {sidebarOpen && (
                        <motion.span
                          className="sidebar-text"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          Import Capture
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/offline"
                    className="sidebar-link"
                  >
                    <motion.span
                      className="sidebar-icon"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <OfflineIcon />
                    </motion.span>
                    <AnimatePresence>
                      {sidebarOpen && (
                        <motion.span
                          className="sidebar-text"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          Offline Mode
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/action-plan"
                    className="sidebar-link"
                  >
                    <motion.span
                      className="sidebar-icon"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ActionPlanIcon />
                    </motion.span>
                    <AnimatePresence>
                      {sidebarOpen && (
                        <motion.span
                          className="sidebar-text"
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          Action Plan
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </NavLink>
                </li>
              </ul>
            </div>
          )}

          {/* Settings & Account Section */}
          {currentUser && (
            <div className="sidebar-section">
              <AnimatePresence>
                {sidebarOpen && (
                  <motion.h3
                    className="sidebar-title"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    Account
                  </motion.h3>
                )}
              </AnimatePresence>
              <ul className="sidebar-menu">
                <li>
                  <NavLink
                    to="/profile"
                    className="sidebar-link"
                  >
                    <motion.span
                      className="sidebar-icon"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <UserIcon />
                    </motion.span>
                    <AnimatePresence>
                      {sidebarOpen && (
                        <motion.span
                          className="sidebar-text"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          Profile
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/privacy"
                    className="sidebar-link"
                  >
                    <motion.span
                      className="sidebar-icon"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ShieldIcon />
                    </motion.span>
                    <AnimatePresence>
                      {sidebarOpen && (
                        <motion.span
                          className="sidebar-text"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          Privacy
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </NavLink>
                </li>
                <li>
                  <button
                    onClick={handleLogout}
                    className="sidebar-link sidebar-logout"
                  >
                    <motion.span
                      className="sidebar-icon"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <LogoutIcon />
                    </motion.span>
                    <AnimatePresence>
                      {sidebarOpen && (
                        <motion.span
                          className="sidebar-text"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          Sign Out
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop - visible when sidebar is open (all screen sizes) */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            className="sidebar-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;